'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { getLocalTimeZone, today } from '@internationalized/date';
import { useLocale, useTranslations } from 'next-intl';
import { useId, useRef, useState } from 'react';
import { type DateValue, I18nProvider } from 'react-aria-components';
import Captcha from 'react-google-recaptcha';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { Button } from '~/components/Button';
import { DatePickerDesktop, DatePickerMobile } from '~/components/DatePicker';
import { TextField } from '~/components/Form/TextField';
import { Box } from '~/components/Layout/Box';
import { Stack } from '~/components/Layout/Stack';
import { PrivacyNotice } from '~/components/MarketingConsentFields';
import { Notification } from '~/components/Notification';
import { useCaptcha } from '~/hooks/useCaptcha';
import { type ReusableNewsletterFormSchema, reusableNewsletterFormSchema } from '~/schemas/reusableNewsletterFormSchema';
import { trackEmailSubmitted } from '~/utils';
import { syncAutofilledInputs } from '~/utils/formHelper';
import styles from './ReusableNewsletterForm.module.css';
import type { ReusableNewsletterFormProps } from './ReusableNewsletterSignup.types';
const ReusableNewsletterForm = ({
  onSubscribe,
  source,
  departureDatePrompt,
  hideDepartureDate = false
}: ReusableNewsletterFormProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const sourceId = useId();
  const departurePromptId = useId();
  const {
    captchaRef,
    onCaptchaErrored,
    executeCaptcha
  } = useCaptcha();
  const formRef = useRef<HTMLFormElement>(null);
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA;
  const minValue = today(getLocalTimeZone());
  const maxValue = minValue.add({
    years: 2
  });
  const methods = useForm<ReusableNewsletterFormSchema>({
    resolver: zodResolver(reusableNewsletterFormSchema, {
      errorMap: (issue, ctx) => {
        const path = issue.path?.join('.');
        const map: Record<string, string> = {
          subscriptionEmail: issue.code === 'too_small' ? t('content.feedback.email_invalid_length') : t('content.feedback.email_invalid_format')
        };
        return {
          message: (path && map[path]) ?? ctx.defaultError
        };
      }
    }),
    mode: 'onBlur',
    defaultValues: {
      subscriptionEmail: '',
      departureDate: null,
      source: source ? source : ''
    }
  });
  const {
    control,
    getValues,
    handleSubmit,
    register,
    setValue,
    resetField,
    formState: {
      isDirty,
      isSubmitting
    }
  } = methods;
  const onSubmit: SubmitHandler<ReusableNewsletterFormSchema> = async data => {
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      // The standard newsletter route takes no captcha token, so a failing widget must not block it.
      const captcha = hideDepartureDate ? '' : await executeCaptcha();
      await onSubscribe({
        email: data.subscriptionEmail,
        departureDate: data.departureDate ? data.departureDate.toString() : undefined,
        captcha: captcha ?? '',
        source: data.source
      });
      trackEmailSubmitted(data.subscriptionEmail, 'newsletter');
      setSuccessMessage(t('content.feedback.reusable_newsletter_subscribe_success'));
      resetField('subscriptionEmail');
      resetField('departureDate');
    } catch (_error) {
      setErrorMessage(t('content.feedback.subscribe_error'));
    } finally {
      captchaRef.current?.reset();
    }
  };
  const onInvalid = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };
  return <form noValidate ref={formRef} className={styles.form} onSubmit={event => {
    syncAutofilledInputs(formRef.current, getValues, setValue);
    return handleSubmit(onSubmit, onInvalid)(event);
  }}>
      <Box spaceTop="--by-space-400">
        <Stack space="--by-space-400">
          {successMessage && <Notification state="success" message={successMessage} />}
          <PrivacyNotice />

          <Controller control={control} name="subscriptionEmail" render={({
          field: {
            name,
            value,
            onChange,
            ref
          },
          fieldState: {
            invalid,
            error
          }
        }) => <TextField name={name} type="email" value={value} onChange={onChange} validationBehavior="aria" isInvalid={invalid} label={t('forms.email_label')} ref={ref} errorMessage={error?.message} isRequired />} />

          {!hideDepartureDate && <>
              <Controller control={control} name="departureDate" render={({
            field: {
              value: value_0,
              onChange: onChange_0,
              onBlur
            },
            fieldState: {
              invalid: invalid_0,
              error: error_0
            }
          }) => {
            const datePickerProps = {
              label: t('forms.departure_date_label'),
              value: value_0 as DateValue | null ?? null,
              onChange: onChange_0,
              onBlur,
              isInvalid: invalid_0,
              errorMessage: error_0?.message,
              minValue,
              maxValue,
              allowDirectInput: true,
              showMonthYearDropdowns: true,
              fullWidth: true,
              labelInside: false,
              'aria-describedby': departureDatePrompt ? departurePromptId : undefined
            };
            return <I18nProvider locale={locale}>
                      <DatePickerDesktop {...datePickerProps} />
                      <DatePickerMobile {...datePickerProps} />
                    </I18nProvider>;
          }} />
              {departureDatePrompt && <p id={departurePromptId} className={styles.departureDatePrompt}>
                  {departureDatePrompt}
                </p>}
            </>}

          <input type="hidden" id={sourceId} {...register('source')} value={source} />
          {recaptchaSiteKey && !hideDepartureDate ? <Captcha ref={captchaRef} size="invisible" sitekey={recaptchaSiteKey} onErrored={onCaptchaErrored} /> : null}
          <Button type="submit" size="small" isLoading={isSubmitting} isDisabled={successMessage !== null && !isDirty} data-ua-click={hideDepartureDate ? 'newsletter-signup' : 'newsletter-signup-diy'}>
            {t('forms.submit')}
          </Button>
          {errorMessage && <Notification state="error" message={errorMessage} />}
        </Stack>
      </Box>
    </form>;
};
export { ReusableNewsletterForm };